/* eslint-disable no-case-declarations */
/* eslint-disable import/prefer-default-export */
import { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import mAuth from 'models/auth';
import useMutation from 'hooks/useMutation';
import { useDispatch, useSelector } from 'react-redux';
import { LOGIN_SUCCESS } from 'constants/ActionTypes';
import _get from 'lodash/get';
import { useToast } from 'components/toast/Toast';

const INITIAL_FIELDS = {
  email: '',
  password: '',
};

const INITIAL_ERRORS = {
  email: null,
  password: null,
};

export const useSignIn = () => {
  const { formatMessage: t } = useIntl();
  const { showToast, toast } = useToast();
  const slugId = useSelector((state) => _get(state, 'cpmSlug.current.result.id', null));

  const dispatch = useDispatch();

  // 📝 Manage fields
  const [fields, setFields] = useState(INITIAL_FIELDS);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [submitStatus, setSubmitStatus] = useState(null);

  // 🔄 Sign in
  const query = mAuth.mutDoLogin;
  const {
    exec: login,
    loading: mutationLoading,
    errors: mutationErrors,
    data: mutationData,
  } = useMutation({
    query,
  });

  // 🔍 Validate fields
  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'email':
        if (!value.trim()) return t({ id: 'formError.email.cantBeBlank' });
        const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!REGEX_EMAIL.test(value)) return t({ id: 'formError.email.badFormat' });
        return '';
      case 'password':
        if (!value.trim()) return t({ id: 'formError.password.cantBeBlank' });
        return '';
      default:
        return '';
    }
  }, []);

  // 📝 Validate form
  const validateForm = useCallback(() => {
    let newErrors = {};

    const errorEmail = validateField('email', fields.email);
    if (errorEmail) newErrors = { ...newErrors, email: errorEmail };

    const errorPassword = validateField('password', fields.password);
    if (errorPassword) newErrors = { ...newErrors, password: errorPassword };

    setErrors(newErrors);
    const isValidated = Object.keys(newErrors).length === 0;
    return isValidated;
  }, [fields, validateField]);

  // 🔁 Change field
  const changeField = ({ name, value }) => {
    setFields((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const resetFields = () => {
    setFields(INITIAL_FIELDS);
    setErrors(INITIAL_ERRORS);
  };

  const handleBlur = ({ name, value }) => {
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // 📬 Submit
  const handleSubmit = async () => {
    dispatch({
      type: 'RECOVER_PASSWORD',
      status: null,
    });

    const isValidated = validateForm();
    if (!isValidated) return;

    const variables = {
      input: {
        slugId,
        email: fields.email,
        password: fields.password,
      },
    };

    // 🚀 Request
    await login(variables);
  };

  // ✅ Manage success
  useEffect(() => {
    if (mutationData && mutationData.login) {
      const { jwt: token } = mutationData.login;
      dispatch({
        type: LOGIN_SUCCESS,
        token,
      });
      setSubmitStatus('success');
    }
  }, [mutationData]);

  /* 🔴 Errors */
  useEffect(() => {
    if (mutationErrors) {
      setSubmitStatus('error');

      // 🛜 Network error
      if (mutationErrors.network && !toast) {
        showToast({
          message: t({ id: 'formError.signInGlobalError' }),
          type: 'alert',
        });
      }

      // 📝 Global form error
      if (mutationErrors.global) {
        setErrors({
          // 💡 We display the error in the password field only
          password: mutationErrors.global,
        });
      }

      // 🔒 Bad credentials errors
      if (mutationErrors.login) {
        setErrors({
          password: t({ id: 'error.login.badCredentials' }),
        });
      }
    }
  }, [_get(mutationErrors, 'global', null) || _get(mutationErrors, 'login', null)]);

  return {
    fields,
    changeField,
    resetFields,
    submitStatus,
    setSubmitStatus,

    errors,
    isSubmitting: mutationLoading,

    handleBlur,
    handleSubmit,
  };
};



// WEBPACK FOOTER //
// ./routes/login/components/cpm_manager/components/SignIn/useSignIn.js